<template>
  <div class="pay-range-identity-fields">
    <div
      v-if="duplicateMessage"
      class="pay-range-identity-fields__alert pay-range-identity-fields__alert--error"
      role="alert"
      data-test="duplicate-identity-alert"
    >
      <v-icon small color="#b42318">mdi-alert-circle-outline</v-icon>
      <span>{{ duplicateMessage }}</span>
    </div>

    <div class="pay-range-identity-fields__field">
      <label class="pay-range-identity-fields__label" for="prd-job-family">
        Job Family Level<span class="pay-range-identity-fields__required">*</span>
      </label>
      <JobFamilyPickerWithAdd
        id="prd-job-family"
        :model-value="modelValue.jobFamily"
        :results="jobFamilies"
        :loading="loadingJobFamily"
        show-level-id
        @update:modelValue="update('jobFamily', $event)"
        @search="$emit('search-job-family', $event)"
        @add-job-family="$emit('add-job-family')"
      />
    </div>

    <div class="pay-range-identity-fields__columns">
      <div class="pay-range-identity-fields__column" data-test="location-column">
        <div class="pay-range-identity-fields__field">
          <label class="pay-range-identity-fields__label" for="prd-location">
            Location<span class="pay-range-identity-fields__required">*</span>
          </label>
          <LocationPicker
            id="prd-location"
            :model-value="modelValue.location"
            :countries="countries"
            :loading="loadingLocation"
            :region-items="regionItems"
            :sub-region-items="subRegionItems"
            persistent-country-label
            @update:modelValue="update('location', $event)"
            @search="$emit('search-location', $event)"
            @clear="$emit('clear-location')"
          />
        </div>
      </div>

      <div class="pay-range-identity-fields__column" data-test="terms-column">
        <div class="pay-range-identity-fields__field">
          <label class="pay-range-identity-fields__label" for="prd-currency">
            Currency<span class="pay-range-identity-fields__required">*</span>
          </label>
          <CurrencyPickerField
            id="prd-currency"
            :value="modelValue.operatingCurrency"
            :currencies="currencies"
            :loading="loadingCurrency"
            @input="update('operatingCurrency', $event)"
            @search="$emit('search-currency', $event)"
          />
        </div>

        <div class="pay-range-identity-fields__field">
          <label class="pay-range-identity-fields__label" for="prd-pay-basis">
            Pay Basis<span class="pay-range-identity-fields__required">*</span>
          </label>
          <v-select
            id="prd-pay-basis"
            class="pay-range-identity-fields__select"
            append-icon="mdi-chevron-down"
            :value="modelValue.payBasis"
            :items="payBasisOptions"
            item-text="name"
            item-value="code"
            outlined
            dense
            hide-details
            @change="update('payBasis', $event)"
          />
        </div>

        <div class="pay-range-identity-fields__field">
          <label class="pay-range-identity-fields__label" for="prd-work-mode">Work mode</label>
          <v-select
            id="prd-work-mode"
            class="pay-range-identity-fields__select"
            append-icon="mdi-chevron-down"
            :value="modelValue.workMode"
            :items="workModeOptions"
            item-text="name"
            item-value="code"
            outlined
            dense
            hide-details
            @change="update('workMode', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import JobFamilyPickerWithAdd from "@components/JobFamilyPickerWithAdd/JobFamilyPickerWithAdd.vue";
import LocationPicker from "@components/LocationPicker/LocationPicker.vue";
import CurrencyPickerField from "@components/CurrencyPickerField/CurrencyPickerField.vue";
import { resolveCurrencyForCountry } from "@components/utils/resolveCurrencyForCountry";

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  countries: { type: Array, default: () => [] },
  currencies: { type: Array, default: () => [] },
  jobFamilies: { type: Array, default: () => [] },
  regionItems: { type: Array, default: () => [] },
  subRegionItems: { type: Array, default: () => [] },
  loadingJobFamily: { type: Boolean, default: false },
  loadingLocation: { type: Boolean, default: false },
  loadingCurrency: { type: Boolean, default: false },
  duplicateMessage: { type: String, default: "" },
  payBasisOptions: {
    type: Array,
    default: () => [
      { id: 1, name: "Hourly", code: "HOURLY" },
      { id: 2, name: "Monthly", code: "MONTHLY" },
      { id: 3, name: "Annually", code: "ANNUAL" },
    ],
  },
  workModeOptions: {
    type: Array,
    default: () => [
      { id: 1, name: "On-Site", code: "ON_SITE" },
      { id: 2, name: "Hybrid", code: "HYBRID" },
      { id: 3, name: "Remote", code: "REMOTE" },
      { id: 4, name: "Unspecified", code: "UNSPECIFIED" },
    ],
  },
});

const emit = defineEmits([
  "update:modelValue",
  "search-job-family",
  "search-location",
  "clear-location",
  "search-currency",
  "add-job-family",
]);

function update(field, value) {
  const next = { ...(props.modelValue || {}), [field]: value };
  if (field === "operatingCurrency") next.currencyTouched = true;
  if (field === "location") applyCountryCurrencyDefault(next);
  emit("update:modelValue", next);
}

// Rule 34: a country change re-defaults Currency until the user has picked or cleared it.
function applyCountryCurrencyDefault(next) {
  const current = props.modelValue || {};
  const previousCountry = current.location?.countryCode ?? null;
  const nextCountry = next.location?.countryCode ?? null;
  if (previousCountry === nextCountry || !nextCountry) return;
  const hasCurrency = Boolean(current.operatingCurrency?.id || current.operatingCurrency?.code);
  if (current.currencyTouched ?? hasCurrency) return;
  const defaulted = resolveCurrencyForCountry({
    currencies: props.currencies,
    countryCode: nextCountry,
    countries: props.countries,
  });
  if (!defaulted && !hasCurrency) return;
  next.operatingCurrency = defaulted;
  next.currencyTouched = false;
}
</script>

<style scoped>
.pay-range-identity-fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}
.pay-range-identity-fields__columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  align-items: start;
}
.pay-range-identity-fields__column {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}
@media (max-width: 959px) {
  .pay-range-identity-fields__columns {
    grid-template-columns: minmax(0, 1fr);
  }
}
.pay-range-identity-fields__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.pay-range-identity-fields__label {
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}
.pay-range-identity-fields__select.v-select.v-text-field--outlined :deep(.v-input__control > .v-input__slot) {
  min-height: 44px;
}
.pay-range-identity-fields__select :deep(.v-input__icon .v-icon) {
  color: #667085;
}
.pay-range-identity-fields__required {
  color: #b42318;
}
.pay-range-identity-fields__alert {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
}
.pay-range-identity-fields__alert--error {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #b42318;
}
</style>
